import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { BRAND_ORANGE } from '../../styles/portalTheme';
import {
  Alert,
  Avatar,
  Box,
  Typography,
  Button,
  CircularProgress,
  TextField,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { StyledTabs, StyledTab } from '../../styles/StyledComponents';
import { useEventQuery } from '../../api/use-event-query';
import { useDeleteEventAttendeeMutation } from '../../api/use-delete-event-attendee-mutation';
import { useRefundBookingMutation } from '../../api/use-refund-booking-mutation';
import LoadingButton from '@mui/lab/LoadingButton';
import { useSubmitPartnerEventMutation } from '../../api/use-submit-partner-event-mutation';
import { useUpdateEventModerationStatusMutation } from '../../api/use-update-event-moderation-status-mutation';
import {
  canEditPartnerEvent,
  canFinalizePartnerEventAttendance,
  canViewPartnerEventAttendance,
  canShowPartnerEventEdit,
  canSubmitPartnerEvent,
  getEventWorkflowStatus,
  useEventsScope,
} from '../../contexts/EventsScopeContext';
import { useFinalizePartnerEventAttendanceMutation } from '../../api/use-finalize-partner-event-attendance-mutation';
import { useArchiveEventMutation } from '../../api/use-archive-event-mutation';
import VerifyAttendanceModal from '../Partner/Events/widgets/VerifyAttendanceModal';
import ArchiveEventDialog from '../Partner/Events/widgets/ArchiveEventDialog';
import { isEventDatePast } from './ManageEvent/eventTimeUtils';
import { mapEventToDuplicateFormState } from './ManageEvent/manageEventForm';
import {
  PRESERVE_LINE_BREAKS_SX,
  SCROLLABLE_DESCRIPTION_SX,
} from './ManageEvent/eventFormConstants';
import { mapApiEventToDetails } from './mapApiEventToDetails';
import { getEventModerationRecords } from './eventModerationRecords';
import { isOnlineEventFormat } from './ManageEvent/eventFormatConstants';
import ScrollableContentWithHint from './widgets/ScrollableContentWithHint';
import EventDetailsBanner from './widgets/EventDetailsBanner';
import EventDetailsQuickInfo from './widgets/EventDetailsQuickInfo';
import EventDetailsContent from './widgets/EventDetailsContent';
import EventGuestListTable from './widgets/EventGuestListTable';
import PartnerEventGuestList from '../Partner/Events/widgets/PartnerEventGuestList';
import PartnerEventStatistics from '../Partner/Events/widgets/PartnerEventStatistics';
import PartnerEventDetailsSidebar from '../Partner/Events/widgets/PartnerEventDetailsSidebar';
import PartnerArchivedEventNotice from '../Partner/Events/widgets/PartnerArchivedEventNotice';
import EventFeedbackModerationPanel from './widgets/EventFeedbackModerationPanel';
import {
  canArchivePartnerEvent,
  isPartnerEventArchived,
  isPartnerEventCompleted,
  shouldHidePartnerEventStatistics,
} from '../Partner/Events/partnerEventArchive';
import { EVENT_DETAILS_TAB_QUERY } from '../../constants/eventNotificationTypes';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

const EVENT_DETAILS_TAB = {
  DETAILS: 'details',
  GUEST_LIST: 'guest-list',
  STATISTICS: 'statistics',
  FEEDBACK: EVENT_DETAILS_TAB_QUERY.FEEDBACK,
};

const getReviewFields = (event) => {
  const isOnline = isOnlineEventFormat(event?.eventFormat);

  const fields = [
    { key: 'title', label: 'Title', eventValueKey: 'title' },
    { key: 'description', label: 'Description', eventValueKey: 'description' },
    { key: 'wingmanDescription', label: 'Short Description', eventValueKey: 'wingmanDescription' },
  ];

  if (!isOnline) {
    fields.push({ key: 'venueName', label: 'Venue Name', eventValueKey: 'venueName' });
  }

  fields.push({ key: 'totalSeats', label: 'Total Seats', eventValueKey: 'totalSeats' });

  return fields;
};
const REVIEW_EDITABLE_STATUSES = new Set(['pending', 'submitted']);
const REVIEW_LOCKED_STATUSES = new Set(['requested', 'approved']);

const INITIAL_FIELD_FEEDBACK_STATE = {
  title: { required: false, severity: 'medium', message: '' },
  description: { required: false, severity: 'high', message: '' },
  wingmanDescription: { required: false, severity: 'medium', message: '' },
  venueName: { required: false, severity: 'medium', message: '' },
  onlineMeetingLink: { required: false, severity: 'medium', message: '' },
  price: { required: false, severity: 'medium', message: '' },
  totalSeats: { required: false, severity: 'medium', message: '' },
};

const DetailsPanel = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(3),
  boxShadow: theme.shadows[1],
}));

const normalizeModerationStatus = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');

const normalizeFeedbackFieldKey = (value) => {
  return String(value ?? '').trim();
};

const hydrateFieldFeedbackFromModeration = (feedback) => {
  const mappedFieldFeedback = { ...INITIAL_FIELD_FEEDBACK_STATE };

  if (!Array.isArray(feedback?.items)) {
    return mappedFieldFeedback;
  }

  feedback.items.forEach((item) => {
    const fieldKey = normalizeFeedbackFieldKey(item?.field);
    if (!mappedFieldFeedback[fieldKey]) return;
    mappedFieldFeedback[fieldKey] = {
      required: true,
      severity:
        String(item?.severity || 'medium')
          .trim()
          .toLowerCase() || 'medium',
      message: String(item?.message || '').trim(),
    };
  });

  return mappedFieldFeedback;
};

const buildRequestChangesItems = (fieldFeedback) =>
  Object.entries(fieldFeedback)
    .filter(([, value]) => value.required)
    .map(([field, value]) => ({
      field,
      severity: String(value.severity || '')
        .trim()
        .toLowerCase(),
      message: String(value.message || '').trim(),
    }))
    .filter((item) => item.field && item.severity && item.message);

const EventDetails = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(EVENT_DETAILS_TAB.DETAILS);
  const [reviewFeedbackDescription, setReviewFeedbackDescription] = useState('');
  const [fieldFeedback, setFieldFeedback] = useState(INITIAL_FIELD_FEEDBACK_STATE);
  const [verifyAttendanceOpen, setVerifyAttendanceOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [fullDetailsOpen, setFullDetailsOpen] = useState(false);
  const { routes, features } = useEventsScope();
  const isReviewMode = searchParams.get('mode') === 'review';
  const isPartnerScope = features.requiresSaveAs;

  const { data, isLoading, isError, error, refetch: refetchEventDetails } = useEventQuery(eventId);

  const submitEventMutation = useSubmitPartnerEventMutation({
    onSuccess: (payload) => {
      toast.success(payload?.message || 'Event submitted for moderation review');
    },
    onError: (err) => {
      toast.error(err?.message || 'Failed to submit event');
    },
  });

  const finalizeAttendanceMutation = useFinalizePartnerEventAttendanceMutation({
    onSuccess: (payload) => {
      toast.success(payload?.message || 'Attendance finalized successfully');
      setVerifyAttendanceOpen(false);
    },
    onError: (err) => {
      toast.error(err?.message || 'Failed to finalize attendance');
    },
  });

  const archiveEventMutation = useArchiveEventMutation();

  const refundMutation = useRefundBookingMutation({
    onSuccess: () => {
      toast.success('Refund processed successfully');
    },
    onError: (err) => {
      toast.error(err?.message || 'Failed to process refund');
    },
  });

  const deleteAttendeeMutation = useDeleteEventAttendeeMutation({
    onSuccess: (result) => {
      toast.success(result.message || 'Attendee removed successfully');
    },
    onError: (err) => {
      toast.error(err?.message || 'Failed to remove attendee');
    },
  });
  const moderationDecisionMutation = useUpdateEventModerationStatusMutation({
    onSuccess: async (_, variables) => {
      if (variables?.payload?.status === 'approved') {
        toast.success('Event approved successfully');
        navigate(routes.list);
      } else {
        toast.success('Changes requested sent to partner');
        await refetchEventDetails();
      }
      setReviewFeedbackDescription('');
      setFieldFeedback(INITIAL_FIELD_FEEDBACK_STATE);
    },
    onError: (err) => {
      toast.error(err?.message || 'Failed to update moderation status');
    },
  });

  const event = useMemo(() => (data ? mapApiEventToDetails(data) : null), [data]);
  const reviewFields = useMemo(
    () =>
      event
        ? getReviewFields(event).map((field) => ({
            key: field.key,
            label: field.label,
            value: event[field.eventValueKey],
          }))
        : [],
    [event],
  );
  const latestModerationResult = getEventModerationRecords(data)[0] ?? null;
  const moderationReviewStatus = normalizeModerationStatus(
    latestModerationResult?.status ?? data?.status,
  );
  const isReviewEditable = REVIEW_EDITABLE_STATUSES.has(moderationReviewStatus);
  const shouldHideReviewButtons = REVIEW_LOCKED_STATUSES.has(moderationReviewStatus);

  useEffect(() => {
    if (!isReviewMode) return;

    const feedback = latestModerationResult?.feedback;
    setFieldFeedback(hydrateFieldFeedbackFromModeration(feedback));
    setReviewFeedbackDescription(feedback?.description?.trim() || feedback?.summary?.trim() || '');
  }, [isReviewMode, latestModerationResult]);

  const hidePartnerStatistics =
    isPartnerScope && Boolean(data) && shouldHidePartnerEventStatistics(data);

  useEffect(() => {
    if (hidePartnerStatistics && tab === EVENT_DETAILS_TAB.STATISTICS) {
      setTab(EVENT_DETAILS_TAB.DETAILS);
    }
  }, [hidePartnerStatistics, tab]);

  useEffect(() => {
    if (!data || isReviewMode) return;
    if (searchParams.get('tab') !== EVENT_DETAILS_TAB_QUERY.FEEDBACK) return;
    setTab(EVENT_DETAILS_TAB.FEEDBACK);
  }, [data, isReviewMode, searchParams]);

  const refundingBookingId = refundMutation.isPending ? refundMutation.variables?.bookingId : null;
  const deletingAttendeeId = deleteAttendeeMutation.isPending
    ? deleteAttendeeMutation.variables?.attendeeId
    : null;

  const handleRefund = (bookingId) => {
    if (!features.allowRefund || !eventId || !bookingId || refundMutation.isPending) return;
    refundMutation.mutate({ eventId, bookingId });
  };

  const handleDeleteAttendee = async (attendeeId) => {
    if (!eventId || !attendeeId || deleteAttendeeMutation.isPending) return;
    await deleteAttendeeMutation.mutateAsync({ eventId, attendeeId });
  };

  if (isLoading) {
    return (
      <Box sx={{ py: 10, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (isError || !event) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
          {error?.message || 'Event not found'}
        </Typography>
        <Button
          onClick={() => navigate(routes.list)}
          sx={{ color: BRAND_ORANGE, textTransform: 'none' }}
        >
          Back to Events
        </Button>
      </Box>
    );
  }

  const workflowStatus = getEventWorkflowStatus(data);
  const isArchivedPartnerEvent = isPartnerScope && isPartnerEventArchived(data);
  const isCompletedPartnerEvent = isPartnerScope && isPartnerEventCompleted(data);
  const canEdit = isPartnerScope
    ? canShowPartnerEventEdit(data, features)
    : canEditPartnerEvent(data, features) && !isEventDatePast(data?.eventDate ?? event?.eventDate);
  const canSubmit = canSubmitPartnerEvent(data);
  const canFinalizeAttendance = canFinalizePartnerEventAttendance(data);
  const canViewAttendance = canViewPartnerEventAttendance(data);
  const showPartnerArchiveAction = isPartnerScope && canArchivePartnerEvent(data);
  const showEditEvent =
    !isReviewMode &&
    !isPartnerScope &&
    !isEventDatePast(data?.eventDate ?? event?.eventDate) &&
    canEditPartnerEvent(data, features);
  const hasRequiredChangesSelected = Object.values(fieldFeedback).some((item) => item.required);
  const showCreatedBy = !isPartnerScope && Boolean(event.createdByPartnerId);
  const createdByLabel = event.createdByName || '—';

  const handleEdit = () => {
    if (!canEdit) {
      toast.error('Event cannot be edited while it is under moderation review.');
      return;
    }
    navigate(routes.edit(eventId));
  };

  const handleDuplicate = () => {
    if (!data) {
      toast.error('Event details are still loading');
      return;
    }
    navigate(routes.new, {
      state: { duplicateForm: mapEventToDuplicateFormState(data) },
    });
    toast.success('Event details copied. Choose a new event date and submit.');
  };

  const handleSubmitForReview = () => {
    if (!eventId || submitEventMutation.isPending) return;
    submitEventMutation.mutate(eventId);
  };

  const handleOpenFinalizeAttendance = () => {
    if (!canFinalizeAttendance && !canViewAttendance) return;
    setVerifyAttendanceOpen(true);
  };

  const handleFinalizeAttendance = (payload) => {
    if (!eventId || finalizeAttendanceMutation.isPending) return;
    finalizeAttendanceMutation.mutate({ eventId, payload });
  };

  const handleOpenArchiveDialog = () => {
    if (!showPartnerArchiveAction) return;
    setArchiveDialogOpen(true);
  };

  const handleArchiveClose = () => {
    if (!archiveEventMutation.isPending) {
      setArchiveDialogOpen(false);
    }
  };

  const handleArchiveConfirm = () => {
    if (!eventId || archiveEventMutation.isPending) return;

    archiveEventMutation.mutate(eventId, {
      onSuccess: async () => {
        setArchiveDialogOpen(false);
        toast.success('Event archived successfully');
        await refetchEventDetails();
      },
      onError: (err) => {
        toast.error(err?.message || 'Failed to archive event');
      },
    });
  };

  const handleApprove = () => {
    if (!eventId || moderationDecisionMutation.isPending) return;
    moderationDecisionMutation.mutate({
      eventId,
      payload: {
        status: 'approved',
      },
    });
  };

  const handleRequestChanges = () => {
    if (!eventId || moderationDecisionMutation.isPending) return;
    const summary = reviewFeedbackDescription.trim();
    if (!summary) {
      toast.error('Overall feedback description is required.');
      return;
    }
    const sanitizedItems = buildRequestChangesItems(fieldFeedback);
    if (sanitizedItems.length === 0) {
      toast.error('Please provide at least one field-level feedback item.');
      return;
    }

    moderationDecisionMutation.mutate({
      eventId,
      payload: {
        status: 'request_changes',
        feedback: {
          summary,
          description: summary,
          items: sanitizedItems,
        },
      },
    });
  };

  const handleFieldFeedbackChange = (field, patch) => {
    setFieldFeedback((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        ...patch,
      },
    }));
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            startIcon={<ArrowBackIosIcon sx={{ fontSize: 14 }} />}
            onClick={() => navigate(routes.list)}
            sx={{
              color: BRAND_ORANGE,
              textTransform: 'none',
              fontWeight: 500,
              p: 0,
              minWidth: 'auto',
              '&:hover': { bgcolor: 'transparent', opacity: 0.85 },
              '& .MuiButton-startIcon': { mr: -0.25, ml: -0.5 },
            }}
          >
            Back
          </Button>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {event.title}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isReviewMode && !isPartnerScope ? (
            <Button
              variant="outlined"
              onClick={() => setFullDetailsOpen(true)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 1.5,
                color: BRAND_ORANGE,
                borderColor: BRAND_ORANGE,
                '&:hover': {
                  borderColor: 'primary.dark',
                  bgcolor: 'rgba(224, 104, 10, 0.04)',
                },
              }}
            >
              View Full Details
            </Button>
          ) : null}
          {/* {canSubmit && features.requiresSaveAs ? (
            <LoadingButton
              variant="contained"
              loading={submitEventMutation.isPending}
              onClick={handleSubmitForReview}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 1.5,
                bgcolor: BRAND_ORANGE,
                boxShadow: 'none',
                '&:hover': { bgcolor: 'primary.dark', boxShadow: 'none' },
              }}
            >
              Submit for Review
            </LoadingButton>
          ) : null} */}
          {showEditEvent ? (
            <Button
              variant="outlined"
              startIcon={<EditOutlinedIcon />}
              onClick={handleEdit}
              disabled={!canEdit}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 1.5,
                color: BRAND_ORANGE,
                borderColor: BRAND_ORANGE,
                '&:hover': {
                  borderColor: 'primary.dark',
                  bgcolor: 'rgba(224, 104, 10, 0.04)',
                },
              }}
            >
              Edit Event
            </Button>
          ) : null}
        </Box>
      </Box>

      {isPartnerScope && !isReviewMode ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 320px' },
            gap: 2.5,
            alignItems: 'start',
          }}
        >
          <DetailsPanel>
            {isArchivedPartnerEvent ? (
              <PartnerArchivedEventNotice archivedAt={data?.deletedAt} />
            ) : null}
            {isCompletedPartnerEvent && !isArchivedPartnerEvent ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                This event is completed. Edit and archive are no longer available.
              </Alert>
            ) : null}
            <EventDetailsBanner event={event} isArchived={isArchivedPartnerEvent} />
            <EventDetailsQuickInfo event={event} />
            {showCreatedBy ? (
              <Box
                sx={{
                  mt: 1.25,
                  mb: 1.75,
                  px: 1.5,
                  py: 1.25,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, color: 'text.secondary', minWidth: 86, flexShrink: 0 }}
                >
                  Created By
                </Typography>
                <Avatar
                  src={event.createdByAvatarUrl || undefined}
                  alt={createdByLabel}
                  sx={{ width: 30, height: 30, fontSize: '0.8rem' }}
                >
                  {createdByLabel !== '—' ? createdByLabel.charAt(0).toUpperCase() : 'P'}
                </Avatar>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {createdByLabel}
                </Typography>
              </Box>
            ) : null}

            <StyledTabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mt: 1 }}>
              <StyledTab label="Event Details" value={EVENT_DETAILS_TAB.DETAILS} />
              <StyledTab label="Guest List" value={EVENT_DETAILS_TAB.GUEST_LIST} />
              {!hidePartnerStatistics ? (
                <StyledTab label="Statistics" value={EVENT_DETAILS_TAB.STATISTICS} />
              ) : null}
              <StyledTab label="Feedback Moderation" value={EVENT_DETAILS_TAB.FEEDBACK} />
            </StyledTabs>

            {tab === EVENT_DETAILS_TAB.DETAILS ? (
              <EventDetailsContent event={event} />
            ) : tab === EVENT_DETAILS_TAB.GUEST_LIST ? (
              <PartnerEventGuestList
                eventId={eventId}
                guests={event.guests}
                onRefund={features.allowRefund ? handleRefund : undefined}
                onDeleteAttendee={handleDeleteAttendee}
                refundingBookingId={refundingBookingId}
                deletingAttendeeId={deletingAttendeeId}
              />
            ) : !hidePartnerStatistics && tab === EVENT_DETAILS_TAB.STATISTICS ? (
              <PartnerEventStatistics eventData={data} />
            ) : tab === EVENT_DETAILS_TAB.FEEDBACK ? (
              <EventFeedbackModerationPanel eventId={eventId} />
            ) : null}
          </DetailsPanel>

          <Box sx={{ position: { lg: 'sticky' }, top: { lg: 16 } }}>
            <PartnerEventDetailsSidebar
              eventData={data}
              canSubmit={canSubmit}
              canFinalizeAttendance={canFinalizeAttendance}
              canViewAttendance={canViewAttendance}
              showEdit={canShowPartnerEventEdit(data, features)}
              submitting={submitEventMutation.isPending || finalizeAttendanceMutation.isPending}
              onSubmit={handleSubmitForReview}
              onFinalizeAttendance={handleOpenFinalizeAttendance}
              onOpenModerationFeedback={() => navigate(routes.moderationFeedback(eventId))}
              onEdit={handleEdit}
              onDuplicate={handleDuplicate}
              onArchive={showPartnerArchiveAction ? handleOpenArchiveDialog : undefined}
            />
          </Box>

          <ArchiveEventDialog
            open={archiveDialogOpen}
            event={{ title: event.title, id: eventId }}
            archiving={archiveEventMutation.isPending}
            onClose={handleArchiveClose}
            onConfirm={handleArchiveConfirm}
          />

          <VerifyAttendanceModal
            open={verifyAttendanceOpen}
            onClose={() => {
              if (!finalizeAttendanceMutation.isPending) setVerifyAttendanceOpen(false);
            }}
            event={data}
            submitting={finalizeAttendanceMutation.isPending}
            onFinalize={handleFinalizeAttendance}
          />
        </Box>
      ) : (
        <DetailsPanel>
          {isReviewMode ? (
            <>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Review Event Details
              </Typography>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                  mb: 2,
                }}
              >
                {reviewFields.map((field, index) => {
                  const isDescriptionField = field.key === 'description';

                  return (
                    <Box
                      key={field.key}
                      sx={{
                        px: 2,
                        py: 1.75,
                        borderBottom: index < reviewFields.length - 1 ? '1px solid' : 'none',
                        borderBottomColor: 'divider',
                        bgcolor: index % 2 ? 'action.hover' : 'background.paper',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', md: '220px minmax(0, 1fr)' },
                          gap: 1.5,
                          alignItems: 'start',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 800,
                            color: 'text.secondary',
                            letterSpacing: '0.01em',
                            textTransform: 'uppercase',
                            fontSize: '0.76rem',
                            pt: 0.75,
                          }}
                        >
                          {field.label}
                        </Typography>
                        {isDescriptionField ? (
                          <ScrollableContentWithHint
                            sx={{
                              p: 1.25,
                              borderRadius: 1.5,
                              border: '1px solid',
                              borderColor: 'divider',
                              bgcolor: 'background.paper',
                              boxShadow: 1,
                              ...SCROLLABLE_DESCRIPTION_SX,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                lineHeight: 1.6,
                                color: 'text.primary',
                                fontWeight: 500,
                                ...PRESERVE_LINE_BREAKS_SX,
                              }}
                            >
                              {field.value || '—'}
                            </Typography>
                          </ScrollableContentWithHint>
                        ) : (
                          <Box
                            sx={{
                              p: 1.25,
                              borderRadius: 1.5,
                              border: '1px solid',
                              borderColor: 'divider',
                              bgcolor: 'background.paper',
                              boxShadow: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                lineHeight: 1.6,
                                color: 'text.primary',
                                fontWeight: 500,
                                whiteSpace: 'pre-wrap',
                              }}
                            >
                              {field.value || '—'}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      <Box
                        sx={{
                          mt: 1.25,
                          p: 1.25,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1.5,
                          bgcolor: (theme) =>
                            fieldFeedback[field.key]?.required
                              ? (theme.portal?.accentSoft ?? theme.palette.action.selected)
                              : theme.palette.background.paper,
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(fieldFeedback[field.key]?.required)}
                              onChange={(e) =>
                                handleFieldFeedbackChange(field.key, {
                                  required: e.target.checked,
                                  message: e.target.checked
                                    ? fieldFeedback[field.key]?.message || ''
                                    : '',
                                })
                              }
                              disabled={moderationDecisionMutation.isPending || !isReviewEditable}
                              sx={{ color: BRAND_ORANGE, '&.Mui-checked': { color: BRAND_ORANGE } }}
                            />
                          }
                          label="Required changes"
                          sx={{ m: 0 }}
                        />
                        {fieldFeedback[field.key]?.required ? (
                          <Box
                            sx={{
                              mt: 1,
                              display: 'grid',
                              gridTemplateColumns: { xs: '1fr', md: '180px minmax(0, 1fr)' },
                              gap: 1,
                            }}
                          >
                            <TextField
                              select
                              label="Severity"
                              id={`review-severity-${field.key}`}
                              value={fieldFeedback[field.key]?.severity || 'medium'}
                              disabled={moderationDecisionMutation.isPending || !isReviewEditable}
                              onChange={(e) =>
                                handleFieldFeedbackChange(field.key, { severity: e.target.value })
                              }
                              SelectProps={{ native: true }}
                            >
                              <option value="high">high</option>
                              <option value="medium">medium</option>
                              <option value="low">low</option>
                            </TextField>
                            <TextField
                              fullWidth
                              id={`review-message-${field.key}`}
                              label="Change request message"
                              placeholder={`Explain what should be updated in ${field.label.toLowerCase()}...`}
                              value={fieldFeedback[field.key]?.message || ''}
                              disabled={moderationDecisionMutation.isPending || !isReviewEditable}
                              onChange={(e) =>
                                handleFieldFeedbackChange(field.key, { message: e.target.value })
                              }
                            />
                          </Box>
                        ) : null}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  bgcolor: 'action.hover',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Moderation Decision
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Overall feedback description"
                  placeholder="Summarize the key updates needed before approval."
                  value={reviewFeedbackDescription}
                  onChange={(e) => setReviewFeedbackDescription(e.target.value)}
                  disabled={moderationDecisionMutation.isPending || !isReviewEditable}
                  sx={{ mb: 1.5 }}
                />
                {!shouldHideReviewButtons ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                    <LoadingButton
                      variant="contained"
                      loading={moderationDecisionMutation.isPending}
                      onClick={handleApprove}
                      disabled={hasRequiredChangesSelected}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 1.5,
                        bgcolor: 'success.main',
                        boxShadow: 'none',
                        '&:hover': { bgcolor: 'success.dark', boxShadow: 'none' },
                      }}
                    >
                      Approve Event
                    </LoadingButton>
                    <LoadingButton
                      variant="outlined"
                      loading={moderationDecisionMutation.isPending}
                      onClick={handleRequestChanges}
                      disabled={!hasRequiredChangesSelected}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 1.5,
                        color: BRAND_ORANGE,
                        borderColor: BRAND_ORANGE,
                        '&:hover': {
                          borderColor: 'primary.dark',
                          bgcolor: 'rgba(224, 104, 10, 0.04)',
                        },
                      }}
                    >
                      Request Changes
                    </LoadingButton>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Review decision already recorded.
                  </Typography>
                )}
              </Box>
            </>
          ) : null}
          {features.requiresSaveAs && workflowStatus ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Status: <strong>{workflowStatus}</strong>
            </Typography>
          ) : null}
          {!isReviewMode ? (
            <>
              <EventDetailsBanner event={event} />
              <EventDetailsQuickInfo event={event} />
              {showCreatedBy ? (
                <Box
                  sx={{
                    mt: 1.25,
                    mb: 1.75,
                    px: 1.5,
                    py: 1.25,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: 'text.secondary', minWidth: 86, flexShrink: 0 }}
                  >
                    Created By
                  </Typography>
                  <Avatar
                    src={event.createdByAvatarUrl || undefined}
                    alt={createdByLabel}
                    sx={{ width: 30, height: 30, fontSize: '0.8rem' }}
                  >
                    {createdByLabel !== '—' ? createdByLabel.charAt(0).toUpperCase() : 'P'}
                  </Avatar>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {createdByLabel}
                  </Typography>
                </Box>
              ) : null}

              <StyledTabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mt: 1 }}>
                <StyledTab label="Event Details" value={EVENT_DETAILS_TAB.DETAILS} />
                <StyledTab label="Guest List" value={EVENT_DETAILS_TAB.GUEST_LIST} />
                <StyledTab label="Feedback Moderation" value={EVENT_DETAILS_TAB.FEEDBACK} />
              </StyledTabs>

              {tab === EVENT_DETAILS_TAB.DETAILS ? (
                <EventDetailsContent event={event} />
              ) : tab === EVENT_DETAILS_TAB.GUEST_LIST ? (
                <EventGuestListTable
                  eventId={eventId}
                  guests={event.guests}
                  showAddAttendee={event.hasPartnerSettings}
                  onRefund={features.allowRefund ? handleRefund : undefined}
                  onDeleteAttendee={handleDeleteAttendee}
                  refundingBookingId={refundingBookingId}
                  deletingAttendeeId={deletingAttendeeId}
                />
              ) : tab === EVENT_DETAILS_TAB.FEEDBACK ? (
                <EventFeedbackModerationPanel eventId={eventId} />
              ) : null}
            </>
          ) : null}
        </DetailsPanel>
      )}

      <Dialog
        open={fullDetailsOpen}
        onClose={() => setFullDetailsOpen(false)}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Event Details</DialogTitle>
        <DialogContent dividers>
          <EventDetailsBanner event={event} />
          <EventDetailsQuickInfo event={event} />
          <EventDetailsContent event={event} />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setFullDetailsOpen(false)}
            sx={{ textTransform: 'none', color: BRAND_ORANGE, fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventDetails;
